/**
 * parts/props.js — throwables and stall targets.
 *
 * COLLIDER CONTRACT: every mass, radius and restitution in this file is GDD
 * tuning transcribed from the greybox. Appearance changes freely; numbers do not.
 *
 * ORIENTATION: anything that becomes a DYNAMIC physics body must not rely on a
 * pre-set `rotation` on the returned object — `physics.syncMeshes` overwrites
 * the mesh quaternion from the body every frame. Where a shape needs turning
 * (the ring's torus, for one) the rotation is baked into the geometry instead.
 *
 * @module assets3d/parts/props
 */

import * as THREE from 'three';
import { ART, shade, tint, rngFor } from '../palette.js';
import {
  canLabel, coconutHusk, baseballStitch, basketballSkin, woodGrain,
  barberStripe, paint, showcardText,
} from '../textures.js';
import { geo, mat, mesh } from './core.js';

/** Radii and masses for each ball kind, in metres and kg. GDD-locked. */
const BALL_SPECS = {
  baseball:   { radius: 0.037, mass: 0.145, color: 0xf4f1e8, rough: 0.8, restitution: 0.32 },
  heavy:      { radius: 0.055, mass: 0.60,  color: 0x6b4423, rough: 0.9, restitution: 0.18 },
  pingpong:   { radius: 0.020, mass: 0.003, color: 0xfffdf5, rough: 0.4, restitution: 0.75 },
  basketball: { radius: 0.108, mass: 0.60,  color: 0xf2762e, rough: 0.85, restitution: 0.60 },
  skee:       { radius: 0.052, mass: 0.28,  color: 0xd9c9a8, rough: 0.7, restitution: 0.35 },
};

/** Surface treatment per ball kind — appearance only. */
function ballMaterial(kind, spec) {
  switch (kind) {
    case 'baseball':
      return mat(0xffffff, { rough: 0.78, map: baseballStitch() });
    case 'basketball':
      return mat(0xffffff, { rough: 0.86, map: basketballSkin() });
    case 'heavy':
      // Lignum-vitae bowling wood: dark, dense, and just glossy enough to
      // catch a highlight so its weight reads at a glance.
      return mat(0xffffff, { rough: 0.42, metal: 0.12, map: woodGrain(0x4e3018) });
    case 'skee':
      return mat(0xffffff, { rough: 0.66, map: woodGrain(0xbfa477) });
    case 'pingpong':
      return mat(spec.color, { rough: 0.3, metal: 0.02 });
    default:
      return mat(spec.color, { rough: spec.rough });
  }
}

/**
 * A throwable ball.
 * @param {'baseball'|'heavy'|'pingpong'|'basketball'|'skee'} kind
 * @returns {THREE.Mesh} with `userData.collider`.
 */
export function createBall(kind = 'baseball') {
  const spec = BALL_SPECS[kind] || BALL_SPECS.baseball;
  const seg = spec.radius < 0.03 ? 12 : 18;
  const m = mesh(
    geo(`ball-${kind}`, () => new THREE.SphereGeometry(spec.radius, seg, Math.round(seg * 0.7))),
    ballMaterial(kind, spec),
  );
  m.name = `ball-${kind}`;
  m.userData.collider = {
    shape: 'ball', radius: spec.radius, mass: spec.mass,
    friction: 0.55, restitution: spec.restitution, ccd: true,
  };
  m.userData.kind = kind;
  return m;
}

// Cans cycle through three dent profiles so a pyramid does not read as a
// cloned row. The counter (not a random draw) keeps the sequence deterministic.
let canVariant = 0;

/**
 * A dented tin can body. The dent is baked into a cached geometry per variant.
 * @param {number} r @param {number} h @param {number} variant
 * @returns {THREE.BufferGeometry}
 */
function cannedBody(r, h, variant) {
  return geo(`can-body-${variant}`, () => {
    const g = new THREE.CylinderGeometry(r, r, h, 20, 3);
    const rnd = rngFor(`can${variant}`);
    const dentAngle = rnd() * Math.PI * 2;
    const dentY = (rnd() - 0.5) * h * 0.5;
    const depth = r * (0.16 + rnd() * 0.12);
    const pos = g.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const a = Math.atan2(v.z, v.x);
      let da = Math.abs(a - dentAngle);
      if (da > Math.PI) da = Math.PI * 2 - da;
      const dy = Math.abs(v.y - dentY);
      const fall = Math.max(0, 1 - da / 0.9) * Math.max(0, 1 - dy / (h * 0.32));
      if (fall > 0) {
        const k = 1 - depth * fall * fall / r;
        v.x *= k;
        v.z *= k;
        pos.setXYZ(i, v.x, v.y, v.z);
      }
    }
    g.computeVertexNormals();
    return g;
  });
}

/**
 * A tin can (GDD 6.2.1: 0.3 kg, friction 0.15).
 * @returns {THREE.Group} with `userData.collider` and `userData.dimensions`.
 */
export function createCan() {
  const r = 0.045;
  const h = 0.12;
  const g = new THREE.Group();
  g.name = 'can';

  const variant = canVariant++ % 3;
  const body = mesh(cannedBody(r, h, variant), mat(0xffffff, {
    rough: 0.42, metal: 0.22, map: canLabel('CHAOS COLA'),
  }));
  g.add(body);

  // Rolled chimes top and bottom — the detail that makes a cylinder a can.
  // Low metalness on purpose: at metal 0.72 the rims went near-black in the
  // stall's warm point light and a pyramid read as a dark smudge at 4.5 m.
  const chimeMat = mat(tint(ART.metal, 0.25), { rough: 0.42, metal: 0.30 });
  for (const s of [-1, 1]) {
    const chime = mesh(
      geo('can-chime', () => new THREE.CylinderGeometry(r * 1.04, r * 1.04, h * 0.07, 20)),
      chimeMat,
    );
    chime.position.y = s * (h / 2 - h * 0.035);
    g.add(chime);
  }

  const lid = mesh(
    geo('can-lid', () => new THREE.CylinderGeometry(r * 0.88, r * 0.95, h * 0.05, 18)),
    mat(shade(ART.metal, 0.18), { rough: 0.35, metal: 0.7 }),
  );
  lid.position.y = h / 2 - h * 0.02;
  g.add(lid);

  g.userData.collider = {
    shape: 'cylinder', radius: r, halfHeight: h / 2,
    mass: 0.3, friction: 0.15, restitution: 0.25, ccd: false,
  };
  g.userData.dimensions = { radius: r, height: h };
  return g;
}

/**
 * A coconut (GDD 6.2.2: 1.5 kg). Husk noise is baked into the geometry so the
 * silhouette is lumpy, not just the shading.
 * @returns {THREE.Mesh} with `userData.collider`.
 */
export function createCoconut() {
  const r = 0.075;
  const m = mesh(
    geo('coconut', () => {
      const g = new THREE.SphereGeometry(r, 16, 12);
      const rnd = rngFor('coconut-shape');
      const pos = g.attributes.position;
      const v = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        const n = 1 + (rnd() - 0.5) * 0.075;
        v.multiplyScalar(n);
        v.y *= 1.10;
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      g.computeVertexNormals();
      return g;
    }),
    mat(0xffffff, { rough: 0.96, map: coconutHusk() }),
  );
  m.name = 'coconut';

  // A few husk fibres catching the light off the silhouette.
  const fibreMat = mat(0x7a5433, { rough: 1, side: THREE.DoubleSide });
  const rnd = rngFor('coconut-fibre');
  for (let i = 0; i < 5; i++) {
    const tuft = mesh(
      geo('coconut-fibre', () => new THREE.ConeGeometry(0.006, 0.028, 4)),
      fibreMat,
      { receive: false },
    );
    const a = rnd() * Math.PI * 2;
    const b = (rnd() - 0.5) * 1.6;
    tuft.position.set(
      Math.cos(a) * Math.cos(b) * r * 0.95,
      Math.sin(b) * r * 0.95,
      Math.sin(a) * Math.cos(b) * r * 0.95,
    );
    tuft.lookAt(tuft.position.clone().multiplyScalar(2));
    tuft.rotateX(Math.PI / 2);
    m.add(tuft);
  }

  m.userData.collider = {
    shape: 'ball', radius: r, mass: 1.5, friction: 0.7, restitution: 0.15, events: true,
  };
  return m;
}

/**
 * A plastic ring for Ring Toss (8 cm inner diameter, GDD 6.3.1).
 * The lie-flat orientation is baked into the geometry — see the header note.
 * @param {number} [color]
 * @returns {THREE.Mesh} with `userData.innerRadius` and `userData.collider`.
 */
export function createRing(color = ART.teal) {
  const inner = 0.04;   // 8 cm inner diameter
  const tube = 0.009;
  const m = mesh(
    geo('ring-flat', () => {
      const g = new THREE.TorusGeometry(inner + tube, tube, 8, 24);
      g.rotateX(Math.PI / 2);
      return g;
    }),
    mat(0xffffff, { rough: 0.38, map: barberStripe(color, ART.cream, 9) }),
  );
  m.name = 'ring';
  m.userData.collider = {
    shape: 'cylinder', radius: inner + tube * 2, halfHeight: tube,
    mass: 0.03, friction: 0.6, restitution: 0.25, linearDamping: 0.6,
  };
  m.userData.innerRadius = inner;
  m.userData.color = color;
  return m;
}

/**
 * A glass bottle target (3.5 cm neck, GDD 6.3.1).
 * @returns {THREE.Group} with `userData.neckY` and `userData.neckRadius`.
 */
export function createBottle() {
  const g = new THREE.Group();
  g.name = 'bottle';
  const glass = mat(0x3f7d4f, {
    rough: 0.08, metal: 0.0, transparent: true, opacity: 0.72,
  });
  // A darker inner volume read through the glass is what stops a transparent
  // cylinder looking like a soap bubble.
  const inner = mat(0x1d4b2c, { rough: 0.3, transparent: true, opacity: 0.85 });

  const body = mesh(
    geo('bottle-body', () => new THREE.CylinderGeometry(0.042, 0.045, 0.2, 16)),
    glass,
  );
  body.position.y = 0.1;
  g.add(body);

  const fill = mesh(
    geo('bottle-fill', () => new THREE.CylinderGeometry(0.036, 0.039, 0.15, 14)),
    inner,
    { cast: false },
  );
  fill.position.y = 0.078;
  g.add(fill);

  const shoulder = mesh(
    geo('bottle-shoulder', () => new THREE.ConeGeometry(0.042, 0.07, 16)),
    glass,
  );
  shoulder.position.y = 0.235;
  g.add(shoulder);

  const neck = mesh(
    geo('bottle-neck', () => new THREE.CylinderGeometry(0.0175, 0.0175, 0.09, 12)),
    glass,
  );
  neck.position.y = 0.305;
  neck.name = 'neck';
  g.add(neck);

  const cap = mesh(
    geo('bottle-cap', () => new THREE.CylinderGeometry(0.021, 0.021, 0.016, 12)),
    mat(ART.red, { rough: 0.45, metal: 0.3 }),
  );
  cap.position.y = 0.352;
  g.add(cap);

  const band = mesh(
    geo('bottle-label', () => new THREE.CylinderGeometry(0.0455, 0.0465, 0.07, 16, 1, true)),
    mat(0xffffff, { rough: 0.8, map: canLabel('POP', { bg: ART.cream, band: ART.red }) }),
  );
  band.position.y = 0.105;
  g.add(band);

  g.userData.neckY = 0.35;
  g.userData.neckRadius = 0.0175;
  g.userData.collider = {
    shape: 'cylinder', radius: 0.045, halfHeight: 0.175, type: 'fixed', friction: 0.5,
  };
  return g;
}

/**
 * A goldfish bowl.
 * @param {'large'|'medium'|'small'} size
 * @returns {THREE.Group} with `userData.mouthRadius` and `userData.rimY`.
 */
export function createBowl(size = 'large') {
  const specs = { large: 0.13, medium: 0.095, small: 0.065 };
  const r = specs[size] ?? specs.large;
  const g = new THREE.Group();
  g.name = `bowl-${size}`;

  const glass = mat(0xcfe9f2, {
    rough: 0.06, metal: 0.0, transparent: true, opacity: 0.34,
    side: THREE.DoubleSide,
  });
  const bowl = mesh(
    geo(`bowl-glass-${size}`, () => new THREE.SphereGeometry(
      r, 20, 14, 0, Math.PI * 2, Math.PI * 0.32, Math.PI * 0.68,
    )),
    glass,
    { receive: false },
  );
  bowl.position.y = r;
  g.add(bowl);

  // A thickened rim gives the glass an edge to catch light on.
  const rim = mesh(
    geo(`bowl-rim-${size}`, () => {
      const t = new THREE.TorusGeometry(r * 0.845, r * 0.045, 6, 22);
      t.rotateX(Math.PI / 2);
      return t;
    }),
    mat(0xe4f4fa, { rough: 0.1, transparent: true, opacity: 0.55 }),
    { receive: false },
  );
  rim.position.y = r * 1.30;
  g.add(rim);

  const water = mesh(
    geo(`bowl-water-${size}`, () => new THREE.CylinderGeometry(
      r * 0.84, r * 0.58, r * 0.72, 18,
    )),
    mat(0x3d8fb0, { rough: 0.08, transparent: true, opacity: 0.55 }),
    { cast: false, receive: false },
  );
  water.position.y = r * 0.66;
  g.add(water);

  // Two offset, near-transparent discs at the waterline. Their overlap breaks
  // the flat top of the water volume into something that reads as a surface.
  for (let i = 0; i < 2; i++) {
    const disc = mesh(
      geo(`bowl-surface-${size}`, () => {
        const c = new THREE.CircleGeometry(r * 0.82, 20);
        c.rotateX(-Math.PI / 2);
        return c;
      }),
      mat(0xbfe6f5, {
        flat: true, transparent: true, opacity: i ? 0.28 : 0.34, depthWrite: false,
      }),
      { cast: false, receive: false },
    );
    disc.position.y = r * 1.02 + i * 0.002;
    disc.rotation.y = i * 0.9;
    disc.scale.setScalar(i ? 0.86 : 1);
    g.add(disc);
  }

  g.userData.mouthRadius = r * 0.82;
  g.userData.rimY = r * 1.32;
  g.userData.size = size;
  g.userData.collider = { shape: 'cylinder', radius: r, halfHeight: r * 0.7, type: 'fixed' };
  return g;
}

/** The painted target square on a backboard. @returns {THREE.CanvasTexture} */
function backboardFace() {
  return paint('backboard-face', 512, 342, (ctx, w, h) => {
    ctx.fillStyle = '#f7f2e6';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = `#${ART.red.toString(16)}`;
    ctx.lineWidth = 12;
    ctx.strokeRect(10, 10, w - 20, h - 20);
    // Regulation target square, bottom-centred over the rim.
    const bw = w * 0.34, bh = h * 0.42;
    ctx.strokeRect((w - bw) / 2, h - bh - 26, bw, bh);
    showcardText(ctx, 'SWISH', w / 2, h * 0.20, {
      size: 46, fill: `#${ART.teal.toString(16)}`, outline: 'rgba(0,0,0,0.25)',
    });
  });
}

/**
 * A basketball hoop with a rope net.
 * @param {number} [rimRadius] 90% of regulation by default.
 * @returns {THREE.Group} with `userData.rimRadius` and `userData.scoreCentre`.
 */
export function createHoop(rimRadius = 0.205) {
  const g = new THREE.Group();
  g.name = 'hoop';

  const backboard = mesh(
    geo('hoop-backboard', () => new THREE.BoxGeometry(1.05, 0.7, 0.045)),
    mat(0xffffff, { rough: 0.45, map: backboardFace() }),
  );
  backboard.position.set(0, 0.35, 0.185);
  g.add(backboard);

  const frame = mesh(
    new THREE.BoxGeometry(1.11, 0.76, 0.03),
    mat(ART.plum, { rough: 0.7 }),
  );
  frame.position.set(0, 0.35, 0.21);
  g.add(frame);

  const rim = mesh(
    geo(`hoop-rim-${rimRadius}`, () => {
      const t = new THREE.TorusGeometry(rimRadius, 0.016, 8, 26);
      t.rotateX(Math.PI / 2);
      return t;
    }),
    mat(0xe8503a, { rough: 0.35, metal: 0.55 }),
  );
  g.add(rim);

  // The bracket tying rim to backboard.
  const bracket = mesh(
    new THREE.BoxGeometry(0.10, 0.05, 0.14),
    mat(0xe8503a, { rough: 0.4, metal: 0.5 }),
  );
  bracket.position.set(0, 0, rimRadius * 0.62);
  g.add(bracket);

  // Rope net as a line lattice: cheap, and it reads as string rather than the
  // translucent cone a cylinder gives you.
  const strands = 12;
  const rings = [0, 0.12, 0.24, 0.36];
  const radiusAt = (d) => rimRadius * (1 - 0.42 * (d / 0.36));
  const pts = [];
  for (let i = 0; i < strands; i++) {
    const a0 = (i / strands) * Math.PI * 2;
    for (let r2 = 0; r2 < rings.length - 1; r2++) {
      const d0 = rings[r2], d1 = rings[r2 + 1];
      const r0 = radiusAt(d0), r1 = radiusAt(d1);
      // Zig-zag: each segment crosses to the neighbouring strand, which is how
      // a real net is knotted and why it reads as mesh at a distance.
      const a1 = a0 + (Math.PI * 2 / strands) * (r2 % 2 === 0 ? 1 : -1);
      pts.push(
        new THREE.Vector3(Math.cos(a0) * r0, -d0, Math.sin(a0) * r0),
        new THREE.Vector3(Math.cos(a1) * r1, -d1, Math.sin(a1) * r1),
      );
    }
  }
  for (const d of rings.slice(1)) {
    const rr = radiusAt(d);
    for (let i = 0; i < strands; i++) {
      const a0 = (i / strands) * Math.PI * 2;
      const a1 = ((i + 1) / strands) * Math.PI * 2;
      pts.push(
        new THREE.Vector3(Math.cos(a0) * rr, -d, Math.sin(a0) * rr),
        new THREE.Vector3(Math.cos(a1) * rr, -d, Math.sin(a1) * rr),
      );
    }
  }
  const net = new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints(pts),
    new THREE.LineBasicMaterial({ color: 0xfdfbf5, transparent: true, opacity: 0.85 }),
  );
  net.name = 'net';
  g.add(net);

  g.userData.rimRadius = rimRadius;
  g.userData.scoreCentre = new THREE.Vector3(0, -0.05, 0);
  return g;
}

/**
 * A dart. Origin at the grip; tip points along −Z.
 * @param {number} [color] Flight colour.
 * @returns {THREE.Group} with `userData.collider`.
 */
export function createDart(color = ART.red) {
  const g = new THREE.Group();
  g.name = 'dart';

  const barrel = mesh(
    geo('dart-barrel', () => {
      const c = new THREE.CylinderGeometry(0.0075, 0.0065, 0.075, 10);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(0x8f949c, { rough: 0.32, metal: 0.75 }),
  );
  g.add(barrel);

  // Knurled grip rings.
  for (let i = 0; i < 3; i++) {
    const ring = mesh(
      geo('dart-knurl', () => {
        const c = new THREE.CylinderGeometry(0.0085, 0.0085, 0.005, 10);
        c.rotateX(Math.PI / 2);
        return c;
      }),
      mat(0x4d525a, { rough: 0.55, metal: 0.6 }),
      { receive: false },
    );
    ring.position.z = -0.012 + i * 0.012;
    g.add(ring);
  }

  const tip = mesh(
    geo('dart-tip', () => {
      const c = new THREE.ConeGeometry(0.0052, 0.038, 8);
      c.rotateX(-Math.PI / 2);
      return c;
    }),
    mat(0xd6dae0, { rough: 0.18, metal: 0.9 }),
  );
  tip.position.z = -0.056;
  g.add(tip);

  const shaft = mesh(
    geo('dart-shaft', () => {
      const c = new THREE.CylinderGeometry(0.004, 0.004, 0.026, 6);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    mat(ART.cream, { rough: 0.5 }),
  );
  shaft.position.z = 0.05;
  g.add(shaft);

  const flightMat = mat(color, { rough: 0.55, side: THREE.DoubleSide });
  for (let i = 0; i < 3; i++) {
    const flight = mesh(
      geo('dart-flight', () => {
        const s = new THREE.Shape();
        s.moveTo(0, -0.016);
        s.lineTo(0.026, -0.020);
        s.lineTo(0.026, 0.018);
        s.lineTo(0, 0.014);
        s.closePath();
        const f = new THREE.ShapeGeometry(s);
        f.rotateY(Math.PI / 2);
        return f;
      }),
      flightMat,
      { receive: false },
    );
    flight.position.z = 0.048;
    flight.rotation.z = (i / 3) * Math.PI * 2;
    g.add(flight);
  }

  g.userData.collider = {
    shape: 'capsule', radius: 0.008, halfHeight: 0.045,
    mass: 0.022, friction: 0.5, restitution: 0.05, ccd: true,
  };
  return g;
}

/** A balloon body: a sphere drawn down to a neck at the bottom. */
function balloonGeo(r) {
  return geo(`balloon-${r}`, () => {
    const g = new THREE.SphereGeometry(r, 16, 14);
    const pos = g.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const t = Math.max(0, -v.y / r);          // 0 at the equator, 1 at the base
      const pinch = 1 - 0.72 * Math.pow(t, 2.6);
      v.x *= pinch;
      v.z *= pinch;
      v.y *= 1.24;
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    g.computeVertexNormals();
    return g;
  });
}

/**
 * An inflated balloon.
 * @param {number|string} [color]
 * @param {{ prize?: boolean }} [opts] A prize balloon carries a star pattern.
 * @returns {THREE.Group} with `userData.radius` and a sensor collider.
 */
export function createBalloon(color = ART.red, opts = {}) {
  const g = new THREE.Group();
  g.name = 'balloon';
  const r = 0.085;

  const skin = opts.prize
    ? mat(0xffffff, { rough: 0.24, map: starPattern(color) })
    : mat(color, { rough: 0.24, metal: 0.02 });

  const body = mesh(balloonGeo(r), skin);
  g.add(body);

  const knot = mesh(
    geo('balloon-knot', () => new THREE.ConeGeometry(0.014, 0.03, 6)),
    mat(shade(typeof color === 'number' ? color : ART.red, 0.2), { rough: 0.3 }),
  );
  knot.position.y = -r * 1.30;
  knot.rotation.x = Math.PI;
  g.add(knot);

  // A soft specular cap. Latex is glossy, and one unlit highlight sells that
  // far more cheaply than raising metalness on every balloon in the wall.
  const gloss = mesh(
    geo('balloon-gloss', () => {
      const c = new THREE.SphereGeometry(r * 0.42, 10, 8);
      c.scale(1, 0.6, 0.55);
      return c;
    }),
    mat(0xffffff, { flat: true, transparent: true, opacity: 0.34, depthWrite: false }),
    { cast: false, receive: false },
  );
  gloss.position.set(-r * 0.36, r * 0.52, -r * 0.52);
  g.add(gloss);

  g.userData.radius = r;
  g.userData.color = color;
  g.userData.prize = !!opts.prize;
  g.userData.collider = { shape: 'ball', radius: r, type: 'fixed', sensor: true };
  return g;
}

/** Star-spangled prize balloon skin. */
function starPattern(color) {
  return paint(`star-balloon-${color}`, 256, 256, (ctx, w, h) => {
    ctx.fillStyle = typeof color === 'number'
      ? `#${color.toString(16).padStart(6, '0')}` : String(color);
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = `#${ART.gold.toString(16)}`;
    const star = (cx, cy, rad) => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const rr = i % 2 ? rad * 0.44 : rad;
        const fn = i ? 'lineTo' : 'moveTo';
        ctx[fn](cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
      }
      ctx.closePath();
      ctx.fill();
    };
    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 4; x++) {
        star(x * 64 + (y % 2 ? 48 : 16), y * 64 + 32, 15);
      }
    }
  });
}
